import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  FileText,
  Tag,
  LayoutDashboard,
  Settings,
  Users,
  GitBranch,
  Plus,
  FilePlus,
  Clock,
  Search,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { SearchGroup, SearchResult } from '@/hooks/useGlobalSearch';

const iconMap = {
  Building2,
  FileText,
  Tag,
  LayoutDashboard,
  Settings,
  Users,
  GitBranch,
  Plus,
  FilePlus,
  Clock,
  Search,
};

interface SearchResultsProps {
  groups: SearchGroup[];
  selectedIndex: number;
  onSelect: (result: SearchResult) => void;
  query: string;
}

export function SearchResults({ groups, selectedIndex, onSelect, query }: SearchResultsProps) {
  const navigate = useNavigate();

  // Calculate total results for index mapping
  const totalResults = groups.reduce((acc, group) => acc + group.results.length, 0);

  if (query.trim() && totalResults === 0) {
    return (
      <div className="px-4 py-8 text-center">
        <Search className="mx-auto h-8 w-8 text-muted-foreground opacity-50 mb-3" />
        <p className="text-sm text-muted-foreground">No results found for "{query}"</p>
        <p className="text-xs text-muted-foreground mt-1">
          Try searching for entities, documents, or pages
        </p>
      </div>
    );
  }

  if (!query.trim() && totalResults === 0) {
    return (
      <div className="px-4 py-8 text-center">
        <Search className="mx-auto h-8 w-8 text-muted-foreground opacity-50 mb-3" />
        <p className="text-sm text-muted-foreground">Start typing to search...</p>
        <p className="text-xs text-muted-foreground mt-1">
          Search entities, documents, brands, and pages
        </p>
      </div>
    );
  }

  let currentIndex = 0;

  return (
    <div className="py-2">
      {groups.map((group, groupIndex) => (
        <div key={group.label} className={groupIndex > 0 ? 'mt-2' : ''}>
          <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            {group.label}
          </div>
          <div>
            {group.results.map((result) => {
              const itemIndex = currentIndex++;
              const isSelected = itemIndex === selectedIndex;
              const Icon = iconMap[result.icon as keyof typeof iconMap] || Search;

              return (
                <button
                  key={result.id}
                  onClick={() => {
                    if (result.action) {
                      result.action();
                    } else if (result.url) {
                      navigate(result.url);
                    }
                    onSelect(result);
                  }}
                  className={`
                    w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors
                    ${
                      isSelected
                        ? 'bg-accent text-accent-foreground'
                        : 'hover:bg-accent/50 text-foreground'
                    }
                  `}
                  data-selected={isSelected}
                >
                  <div
                    className={`
                    flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-md
                    ${isSelected ? 'bg-primary/10' : 'bg-muted'}
                  `}
                  >
                    <Icon
                      className={`w-4 h-4 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`}
                    />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">
                        {highlightMatch(result.title, query)}
                      </span>
                      {result.type && (
                        <Badge variant="outline" className="text-xs capitalize flex-shrink-0">
                          {result.type}
                        </Badge>
                      )}
                    </div>
                    {result.subtitle && (
                      <p className="text-xs text-muted-foreground truncate mt-0.5">
                        {highlightMatch(result.subtitle, query)}
                      </p>
                    )}
                  </div>

                  {isSelected && (
                    <div className="flex-shrink-0 text-xs text-muted-foreground">
                      <kbd className="px-1.5 py-0.5 text-xs font-mono bg-muted border border-border rounded">
                        Enter
                      </kbd>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// Helper function to highlight matching text
function highlightMatch(text: string, query: string): React.ReactNode {
  if (!query.trim()) {
    return text;
  }

  const parts = text.split(new RegExp(`(${escapeRegExp(query)})`, 'gi'));

  return (
    <>
      {parts.map((part, index) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={index} className="bg-yellow-200 dark:bg-yellow-900/50 text-foreground">
            {part}
          </mark>
        ) : (
          <span key={index}>{part}</span>
        )
      )}
    </>
  );
}

function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
